import React, { useMemo } from 'react'
import { Button, DatePicker, Tooltip } from '@surecompanies/core_components'
import { Input, Select } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useAllowedBulkStatuses, ALL_FIELD_KEYS } from './bulkStatus.constants'
import {
  getRowErrors,
  isFieldActiveForRow,
  isFieldRequiredForRow,
} from './bulkStatus.helpers'
import type { ParsedRow } from './bulkStatus.types'
import {
  STATUS_FIELD_CONFIG,
  StatusUpdateFormKey,
  StatusUpdateFormKeyLabels,
} from '../PaymentAssignment/CaseManagement/statusUpdate.type'
import {
  PaymentAssignmentStatusId,
  getPaymentAssignmentStatusLabel,
} from '../PaymentAssignment/Utils/types'

interface UsePreviewColumnsArgs {
  isSubmitting: boolean
  rowErrors: Record<string, string>
  duplicateBenefitIds: ReadonlySet<string>
  onBenefitIdChange: (key: string, value: string) => void
  onStatusChange: (key: string, value: PaymentAssignmentStatusId) => void
  onNoteChange: (key: string, value: string) => void
  onFieldChange: (
    key: string,
    fieldKey: StatusUpdateFormKey,
    value: string
  ) => void
  onDeleteRow: (key: string) => void
}

/** Fixed columns (checkmark, benefitId, status, note) + status-specific field width + delete column. */
export const PREVIEW_TOTAL_SCROLL_WIDTH =
  36 + 260 + 210 + 180 + ALL_FIELD_KEYS.length * 155 + 40

/**
 * Builds the Ant Design `Table` column definitions for the preview/edit step.
 * Kept as a hook (rather than a plain function) since the columns memoize on
 * the callbacks/rowErrors that change as the user edits the grid.
 */
export function usePreviewColumns({
  isSubmitting,
  rowErrors,
  duplicateBenefitIds,
  onBenefitIdChange,
  onStatusChange,
  onNoteChange,
  onFieldChange,
  onDeleteRow,
}: UsePreviewColumnsArgs) {
  const allowedStatuses = useAllowedBulkStatuses()
  return useMemo(() => {
    const fixed = [
      {
        title: '',
        key: '_valid',
        width: 36,
        fixed: 'left' as const,
        render: (_: unknown, row: ParsedRow) => {
          const isDuplicate = duplicateBenefitIds.has(
            row.benefitId.trim().toLowerCase()
          )
          if (isDuplicate) {
            return (
              <WarningOutlined style={{ color: '#faad14', fontSize: 14 }} />
            )
          }
          const errs = getRowErrors(row, duplicateBenefitIds, allowedStatuses)
          return errs.length === 0 ? (
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 14 }} />
          ) : (
            <Tooltip title={errs.join('\n')}>
              <CloseCircleOutlined
                style={{ color: '#ff4d4f', fontSize: 14, cursor: 'help' }}
              />
            </Tooltip>
          )
        },
      },
      {
        title: 'Benefit ID',
        key: 'benefitId',
        dataIndex: 'benefitId',
        width: 260,
        fixed: 'left' as const,
        render: (value: string, row: ParsedRow) => {
          const backendError = rowErrors[row.key]
          const isDuplicate = duplicateBenefitIds.has(
            value.trim().toLowerCase()
          )
          const duplicateError = isDuplicate
            ? 'Duplicate Benefit ID (same ID used on another row)'
            : undefined
          return (
            <div>
              <input
                style={{
                  border: `1px solid ${backendError || isDuplicate ? '#ff4d4f' : '#d9d9d9'}`,
                  borderRadius: 4,
                  padding: '2px 6px',
                  width: '100%',
                  fontSize: 12,
                  fontFamily: 'monospace',
                }}
                value={value}
                placeholder="benefit-uuid"
                onChange={(e) => onBenefitIdChange(row.key, e.target.value)}
              />
              {(backendError || duplicateError) && (
                <Tooltip title={backendError || duplicateError}>
                  <div
                    style={{
                      color: '#ff4d4f',
                      fontSize: 11,
                      marginTop: 2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: 'help',
                    }}
                  >
                    {backendError || duplicateError}
                  </div>
                </Tooltip>
              )}
            </div>
          )
        },
      },
      {
        title: 'Status',
        key: 'status',
        dataIndex: 'status',
        width: 210,
        fixed: 'left' as const,
        render: (value: string, row: ParsedRow) => (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={
              (value || undefined) as PaymentAssignmentStatusId | undefined
            }
            placeholder="Select status"
            onChange={(v: PaymentAssignmentStatusId) =>
              onStatusChange(row.key, v)
            }
            options={allowedStatuses.map((s) => ({
              value: s,
              label: getPaymentAssignmentStatusLabel(s),
            }))}
          />
        ),
      },
      {
        title: 'Note',
        key: 'note',
        dataIndex: 'note',
        width: 180,
        render: (value: string, row: ParsedRow) => (
          <Input
            size="small"
            value={value}
            placeholder="Optional note"
            onChange={(e) => onNoteChange(row.key, e.target.value)}
          />
        ),
      },
    ]

    const fieldCols = ALL_FIELD_KEYS.map((fieldKey) => ({
      title: (
        <Tooltip title={fieldKey}>
          <span style={{ fontSize: 11 }}>
            {StatusUpdateFormKeyLabels[fieldKey]}
          </span>
        </Tooltip>
      ),
      key: fieldKey,
      width: 155,
      render: (_: unknown, row: ParsedRow) => {
        const active = isFieldActiveForRow(row, fieldKey)
        if (!active) {
          return (
            <div
              style={{
                background: '#f0f0f0',
                borderRadius: 3,
                height: 26,
                width: '100%',
              }}
            />
          )
        }

        const required = isFieldRequiredForRow(row, fieldKey)
        const rules =
          STATUS_FIELD_CONFIG[row.status as PaymentAssignmentStatusId] ?? []
        const rule = rules.find((r) => r.key === fieldKey)
        const value = row.fields[fieldKey] ?? ''
        const hasError = required && !value

        // If the rule carries a discrete options list, render a Select
        if (rule?.options && rule.options.length > 0) {
          const selectOptions = rule.options.map((o) => ({
            value: o,
            label: o,
          }))
          return (
            <Select
              size="small"
              style={{ width: '100%' }}
              value={value === '' ? undefined : value}
              placeholder={required ? '* req.' : 'opt.'}
              onChange={(v: string) => onFieldChange(row.key, fieldKey, v)}
              status={hasError ? 'error' : undefined}
              showSearch
              optionFilterProp="label"
              options={selectOptions}
            />
          )
        }

        const inputType =
          rule?.type === 'number' || rule?.type === 'day' ? 'number' : 'text'

        if (rule?.type === 'date') {
          const dateValue = value
            ? dayjs(value, ['MM-DD-YYYY', 'YYYY-MM-DD'])
            : null
          return (
            <DatePicker
              size="small"
              style={{ width: '100%' }}
              format="MM-DD-YYYY"
              value={dateValue?.isValid() ? dateValue : null}
              placeholder={required ? '* required' : 'optional'}
              status={hasError ? 'error' : undefined}
              onChange={(d) =>
                onFieldChange(
                  row.key,
                  fieldKey,
                  d ? d.format('MM-DD-YYYY') : ''
                )
              }
            />
          )
        }

        return (
          <input
            type={inputType}
            style={{
              border: `1px solid ${hasError ? '#ff4d4f' : '#d9d9d9'}`,
              borderRadius: 4,
              padding: '2px 6px',
              width: '100%',
              fontSize: 12,
              background: '#fff',
            }}
            value={value}
            placeholder={required ? '* required' : 'optional'}
            onChange={(e) => onFieldChange(row.key, fieldKey, e.target.value)}
          />
        )
      },
    }))

    const deleteCol = {
      title: '',
      key: '_del',
      width: 40,
      render: (_: unknown, row: ParsedRow) => (
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined />}
          disabled={isSubmitting}
          onClick={() => onDeleteRow(row.key)}
        />
      ),
    }

    return [...fixed, ...fieldCols, deleteCol]
  }, [
    isSubmitting,
    onBenefitIdChange,
    onStatusChange,
    onNoteChange,
    onFieldChange,
    onDeleteRow,
    rowErrors,
    duplicateBenefitIds,
    allowedStatuses,
  ])
}
